import { addDays, addMonths, format, parse, parseISO } from "date-fns";
import color from "@constants/color";
import { GIGS_COPY } from "./copy";
import type {
  GigDay,
  GigDuty,
  GigSummary,
  GigsAvailabilitySubmitDay,
  HistoryDayState,
  HomeWidgetDayStatus,
  LocalSelection,
} from "./types";

/**
 * Pure helpers for the Gigs feature. Nothing here touches the device clock to
 * decide a lock/cutoff — the server is the sole source of truth for
 * `is_locked` / `lock_at` / `lock_time` / history `state`. These functions
 * only format what the API already sent, or diff/derive from local taps.
 *
 * User-facing sentences live in `./copy` (English only). This module stays
 * store-free, so it is jest-testable with no redux/store mocking.
 */

// ---------------------------------------------------------------------------
// Time / date labels
// ---------------------------------------------------------------------------

/** "20:00" -> "8 PM", "18:30" -> "6:30 PM", "06:00" -> "6 AM". Never hardcode a time string. */
export const formatTimeLabel = (
  time24: string | null | undefined,
): string | null => {
  if (!time24) return null;
  try {
    const parsed = parse(time24, "HH:mm", new Date());
    if (Number.isNaN(parsed.getTime())) return null;
    // Only show minutes when they're non-zero — "8 PM", but "6:30 PM".
    return format(parsed, parsed.getMinutes() === 0 ? "h a" : "h:mm a");
  } catch {
    return null;
  }
};

/** "2026-09-10" -> "Thu 10 Sep" — used for full row labels. */
export const formatFullDate = (dateIso: string): string =>
  format(parseISO(dateIso), "EEE d MMM");

/** The device's local date as "YYYY-MM-DD". Display only — never a lock. */
export const localTodayIso = (now: Date = new Date()): string =>
  format(now, "yyyy-MM-dd");

/**
 * "Today, Sat 19 Sep" / "Tomorrow, Sun 20 Sep" / "Mon 21 Sep". Relative to the
 * device date rather than the window's first day: today's gig stays in the
 * window until early morning, so the first window day is not always tomorrow.
 * Only the word depends on the device clock; locks stay server-side.
 */
export const formatGigDayLabel = (
  gigDate: string,
  todayIso: string,
): string => {
  const full = formatFullDate(gigDate);
  if (gigDate === todayIso) return `${GIGS_COPY.date.today}, ${full}`;
  const tomorrowIso = format(addDays(parseISO(todayIso), 1), "yyyy-MM-dd");
  if (gigDate === tomorrowIso) return `${GIGS_COPY.date.tomorrow}, ${full}`;
  return full;
};

/** "2026-09" -> "September 2026" */
export const formatMonthLabel = (month: string): string =>
  format(parseISO(`${month}-01`), "MMMM yyyy");

/** "2026-09" shifted by `delta` months, e.g. shiftMonth("2026-09", -1) -> "2026-08" */
export const shiftMonth = (month: string, delta: number): string =>
  format(addMonths(parseISO(`${month}-01`), delta), "yyyy-MM");

/** "YYYY-MM" strings sort lexically, so plain string comparison is safe. */
export const canGoPrevMonth = (month: string, minMonth: string): boolean =>
  month > minMonth;

export const canGoNextMonth = (month: string, maxMonth: string): boolean =>
  month < maxMonth;

// ---------------------------------------------------------------------------
// Marking screen — duty subheader pieces
// ---------------------------------------------------------------------------

export interface DutySubheaderParts {
  shiftLabel: string | null; // e.g. "6 AM – 2 PM"
  locationName: string | null;
}

/**
 * Formats the shift-time half of "Duty 6 AM – 2 PM · DS Sector 63" and passes
 * `location_name` through untouched. The component decides how to join them
 * and hides the whole line when both are null — kept here only because it's
 * pure time-formatting, not copy.
 */
export const buildDutySubheaderParts = (
  duty:
    | Pick<GigDuty, "location_name" | "shift_start" | "shift_end">
    | null
    | undefined,
): DutySubheaderParts => {
  if (!duty) return { shiftLabel: null, locationName: null };
  const startLabel = formatTimeLabel(duty.shift_start);
  const endLabel = formatTimeLabel(duty.shift_end);
  let shiftLabel: string | null = null;
  if (startLabel && endLabel) shiftLabel = `${startLabel} – ${endLabel}`;
  else shiftLabel = startLabel ?? endLabel ?? null;
  return { shiftLabel, locationName: duty.location_name ?? null };
};

// ---------------------------------------------------------------------------
// Local selection diff / summary
// ---------------------------------------------------------------------------

/**
 * Days whose LOCAL selection differs from the server's saved response, among
 * unlocked days only — exactly the payload the Confirm submit sends, and the
 * same set that drives whether Confirm is enabled.
 */
export const computeChangedDays = (
  localSelections: Record<string, LocalSelection>,
  serverDays: GigDay[],
): GigsAvailabilitySubmitDay[] => {
  const changed: GigsAvailabilitySubmitDay[] = [];
  serverDays.forEach((day) => {
    if (day.is_locked) return;
    const local = localSelections[day.gig_date];
    if (!local || local === "UNSET") return;
    if (local === day.response) return;
    changed.push({ gig_date: day.gig_date, response: local });
  });
  return changed;
};

/** Confirm is enabled only when there's at least one real change, and never while submitting. */
export const isConfirmEnabled = (
  localSelections: Record<string, LocalSelection>,
  serverDays: GigDay[],
  submitting: boolean,
): boolean => {
  if (submitting) return false;
  return computeChangedDays(localSelections, serverDays).length > 0;
};

/**
 * The sticky footer's running "N yes · N no · N pending" — derived from the
 * CURRENT effective answer per day (a local edit if present, else the
 * server's saved value), recomputed as the user taps.
 */
export const deriveLocalSummary = (
  localSelections: Record<string, LocalSelection>,
  serverDays: GigDay[],
): GigSummary => {
  return serverDays.reduce<GigSummary>(
    (acc, day) => {
      const local = localSelections[day.gig_date];
      const effective = local && local !== "UNSET" ? local : day.response;
      if (effective === "YES") acc.yes += 1;
      else if (effective === "NO") acc.no += 1;
      else acc.pending += 1;
      return acc;
    },
    { yes: 0, no: 0, pending: 0 },
  );
};

/** How many days the POST response skipped as already-locked — 0 means no note is needed. */
export const getSkippedLockedCount = (
  skippedLocked: string[] | null | undefined,
): number => skippedLocked?.length ?? 0;

// ---------------------------------------------------------------------------
// Home widget card
// ---------------------------------------------------------------------------

export const getHomeWidgetDotColor = (status: HomeWidgetDayStatus): string => {
  switch (status) {
    case "ON_DUTY":
      return color.gigsOnDutyGreen;
    case "PENDING":
      return color.gigsPendingAmber;
    case "OFF":
    case "NOT_MARKED":
    default:
      return color.gigsOffGrey;
  }
};

// ---------------------------------------------------------------------------
// History calendar
// ---------------------------------------------------------------------------

export interface HistoryDayStyle {
  backgroundColor: string;
  textColor: string;
  dashed: boolean;
  borderColor?: string;
}

export const getHistoryDayStyle = (state: HistoryDayState): HistoryDayStyle => {
  switch (state) {
    case "WORKED":
      return {
        backgroundColor: color.gigsWorkedGreen,
        textColor: color.white,
        dashed: false,
      };
    case "NO_SHOW":
      return {
        backgroundColor: color.gigsNoShowRed,
        textColor: color.white,
        dashed: false,
      };
    case "UPCOMING_YES":
      return {
        backgroundColor: color.gigsUpcomingYesBg,
        textColor: color.mediumBlack,
        dashed: false,
      };
    // Neutral "Marked yes" look — used whenever the outcome isn't resolved yet
    // (always the case while show_outcomes is false).
    case "AWAITING_OUTCOME":
      return {
        backgroundColor: color.white,
        textColor: color.mediumBlack,
        dashed: true,
        borderColor: color.gigsWorkedGreen,
      };
    case "OFF":
      return {
        backgroundColor: color.gigsOffBg,
        textColor: color.darkGrey,
        dashed: false,
      };
    case "NOT_MARKED":
    default:
      return {
        backgroundColor: "transparent",
        textColor: color.mediumBlack,
        dashed: false,
      };
  }
};

/**
 * Only the states that can actually appear are shown — with show_outcomes
 * false, Worked/No-show never occur (the backend sends AWAITING_OUTCOME
 * instead), so their legend entries are omitted entirely rather than shown
 * disabled. Labels are resolved by the caller via `t()`.
 */
export const getHistoryLegendStates = (
  showOutcomes: boolean,
): HistoryDayState[] => {
  if (showOutcomes) {
    return ["UPCOMING_YES", "WORKED", "NO_SHOW", "OFF", "NOT_MARKED"];
  }
  return ["UPCOMING_YES", "AWAITING_OUTCOME", "OFF", "NOT_MARKED"];
};
